import React, { useEffect, useState } from 'react';
import { RootState } from '@/store';
import { useSelector } from 'react-redux';

import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { useAuth } from '@/hooks/useAuth';
import { LoginNotificationModal } from './LoginNotificationModal';

interface AuthWrapperProps {
  children: React.ReactNode;
}

export const AuthWrapper = ({ children }: AuthWrapperProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const refinanceEnabled = useSelector(
    (state: RootState) => state.mode.refinanceEnabled
  );
  const isInvestorMode = useSelector(
    (state: RootState) => state.mode.isInvestorMode
  );
  const [showLoginNotification, setShowLoginNotification] = useState(false);
  // Persisted (not just component state) so a full page reload/direct nav to
  // /properties/add or /loan/qualifications within the 24h window doesn't
  // re-trigger this — AuthWrapper remounting reset in-memory state before.
  const [hasShownNotification, setHasShownNotification] = useState(() => {
    try {
      return localStorage.getItem('loginNotificationShown') === 'true';
    } catch {
      return false;
    }
  });

  const { data: user, isLoading: userLoading } = useGetCurrentUserQuery(
    undefined,
    {
      skip: !isAuthenticated,
    }
  );

  const isNewUser = (u: { createdAt?: string } | undefined) => {
    if (!u?.createdAt) return false;
    const createdAt = new Date(u.createdAt);
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return createdAt > twentyFourHoursAgo;
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoading &&
      !userLoading &&
      user &&
      !hasShownNotification &&
      // New accounts go through onboarding, which already gets them to their
      // first property — a welcome popup on top of it was one more screen in
      // the way. Only legacy accounts (no onboarding flag) still see it.
      user.onboardingCompleted == null &&
      isNewUser(user)
    ) {
      setShowLoginNotification(true);
      setHasShownNotification(true);
      try {
        localStorage.setItem('loginNotificationShown', 'true');
      } catch {
        // ignore storage failures (private browsing, quota, etc.)
      }
    }
  }, [isAuthenticated, isLoading, userLoading, user, hasShownNotification]);

  const handleCloseNotification = () => {
    setShowLoginNotification(false);
  };

  return (
    <>
      {children}
      <LoginNotificationModal
        open={showLoginNotification}
        onClose={handleCloseNotification}
        refinanceEnabled={refinanceEnabled}
        isInvestorMode={isInvestorMode}
      />
    </>
  );
};
